import { useMemo } from 'react';
import { shallow } from 'zustand/shallow';
import {
  Section,
  SidebarNode,
  rootKeyOf,
  useSidebarTreeStore,
} from '../../../../store/storeSidebarTree';

const EMPTY: readonly string[] = Object.freeze([]);

export const useNode = (id: string | null | undefined): SidebarNode | null => {
  return useSidebarTreeStore(s => (id ? s.nodes[id] ?? null : null));
};

export const useChildrenIds = (
  parentId: string | null | undefined,
): readonly string[] => {
  return useSidebarTreeStore(
    s => (parentId ? s.childrenByParent[parentId] ?? EMPTY : EMPTY),
    shallow,
  );
};

export const useRootIds = (
  section: Section,
  spaceId: string | null,
): readonly string[] => {
  const key = rootKeyOf(section, spaceId);
  return useSidebarTreeStore(s => s.rootBySection[key] ?? EMPTY, shallow);
};

export const useChildrenNodes = (
  parentId: string | null | undefined,
): SidebarNode[] => {
  const ids = useChildrenIds(parentId);
  const nodes = useSidebarTreeStore(s => s.nodes);
  return useMemo(
    () =>
      ids.flatMap(id => {
        const n = nodes[id];
        return n ? [n] : [];
      }),
    [ids, nodes],
  );
};

export const useRootNodes = (
  section: Section,
  spaceId: string | null,
): SidebarNode[] => {
  const ids = useRootIds(section, spaceId);
  const nodes = useSidebarTreeStore(s => s.nodes);
  return useMemo(
    () =>
      ids.flatMap(id => {
        const n = nodes[id];
        return n ? [n] : [];
      }),
    [ids, nodes],
  );
};

export const useIsPending = (id: string): boolean =>
  useSidebarTreeStore(s => s.pendingMoves.has(id));

export const getSiblingIds = (
  state: ReturnType<typeof useSidebarTreeStore.getState>,
  parentId: string | null,
  section: Section,
  spaceId: string | null,
): string[] => {
  if (parentId) return [...(state.childrenByParent[parentId] ?? [])];
  return [...(state.rootBySection[rootKeyOf(section, spaceId)] ?? [])];
};
